import React, { useState, useEffect, useCallback, useRef } from 'react';
import { websocketVirtualAgent } from 'components/virtualagentws/websocketVirtualAgent';
import { Col, Row } from 'react-bootstrap';
import Button from 'components/base/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  getVirtualAgentWsUrl,
  getAuthenticatedWsUrl
} from 'services/virtualagent.service';
import {
  faMicrophone,
  faMicrophoneSlash,
  faPhoneSlash,
  faPhoneVolume,
  faCar
} from '@fortawesome/free-solid-svg-icons';

interface ConversationMessage {
  source: 'user' | 'ai';
  message: string;
}

// Define vehicle information interface
interface VehicleInfo {
  licensePlate: string;
  mileage: number;
  serviceHistory?: string;
  tireSize?: string;
  serviceType?: 'maintenance' | 'tires';
  preferredArea?: string;
  appointmentDate?: string;
  appointmentTime?: string;
  workshopName?: string;
  contactPhone?: string;
}

// Define feedback message interface
export interface FeedbackMessage {
  id: string;
  timestamp: Date;
  type: 'info' | 'validation' | 'transfer' | 'close' | 'appointment';
  message: string;
  details?: string;
  success?: boolean;
}

type HVAAyvensConversationWidgetProps = {
  agentId: string;
  agentName: string;
  onError?: (error: Error) => void;
  onFeedback: (message: Omit<FeedbackMessage, 'id' | 'timestamp'>) => void;
  firstMessage: string;
  prompt: string;
  voice: string;
  language: string;
  vehicleInfo: VehicleInfo;
  onAppointmentScheduled: () => void;
  onServiceTypeSelected: (type: string) => void;
};

const HVAAyvensConversationWidget = ({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  agentId,
  agentName,
  onError,
  onFeedback,
  firstMessage,
  prompt,
  voice,
  language,
  vehicleInfo,
  onAppointmentScheduled,
  onServiceTypeSelected
}: HVAAyvensConversationWidgetProps): JSX.Element => {
  const [hasPermission, setHasPermission] = useState<boolean>(false);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [clientToolsConfig, setClientToolsConfig] = useState({});
  const [isLicensePlateValidated, setIsLicensePlateValidated] = useState(false);
  const [isMileageValidated, setIsMileageValidated] = useState(false);

  // Reference for message container
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Function to scroll to the last message
  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      const scrollContainer = messagesContainerRef.current;
      scrollContainer.scrollTop = scrollContainer.scrollHeight;
    }
  };

  // Function to generate available appointment slots
  const generateAvailableSlots = (preferredArea: string) => {
    const today = new Date();
    const slots: {
      date: string;
      time: string;
      workshop: string;
      dayOfWeek: string;
    }[] = [];
    const workshops = {
      western: {
        name: 'AutoService Workshop',
        times: ['09:00 AM', '11:00 AM', '02:00 PM', '04:00 PM']
      },
      southern: {
        name: 'SouthAuto Workshop',
        times: ['10:00 AM', '12:00 PM', '03:00 PM']
      },
      northern: {
        name: 'NorthPoint Service',
        times: ['08:30 AM', '10:30 AM', '01:00 PM', '03:30 PM']
      },
      eastern: {
        name: 'EastEnd Auto',
        times: ['09:30 AM', '11:30 AM', '02:30 PM']
      },
      default: {
        name: 'Central Auto Service',
        times: ['09:00 AM', '11:00 AM', '02:00 PM', '04:00 PM']
      }
    };

    // Determine which workshop(s) to use based on area
    const workshopsToUse = [];
    const areaLower = preferredArea.toLowerCase();

    if (areaLower.includes('western') || areaLower.includes('west')) {
      workshopsToUse.push({ area: 'western', ...workshops.western });
    } else if (areaLower.includes('southern') || areaLower.includes('south')) {
      workshopsToUse.push({ area: 'southern', ...workshops.southern });
    } else if (areaLower.includes('northern') || areaLower.includes('north')) {
      workshopsToUse.push({ area: 'northern', ...workshops.northern });
    } else if (areaLower.includes('eastern') || areaLower.includes('east')) {
      workshopsToUse.push({ area: 'eastern', ...workshops.eastern });
    } else {
      // If no specific area or unknown area, show multiple options
      workshopsToUse.push(
        { area: 'western', ...workshops.western },
        { area: 'southern', ...workshops.southern }
      );
    }

    // Generate slots for the next 14 days
    for (let i = 1; i <= 14; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);

      // Skip weekends
      if (date.getDay() === 0 || date.getDay() === 6) continue;

      // For each workshop
      workshopsToUse.forEach(workshop => {
        // Add only the first 2 time slots for each day to keep it manageable
        const timesToAdd =
          i <= 7 ? workshop.times.slice(0, 2) : workshop.times.slice(0, 1);

        timesToAdd.forEach(time => {
          slots.push({
            date: date.toLocaleDateString('en-US', {
              month: 'long',
              day: 'numeric',
              year: 'numeric'
            }),
            time: time,
            workshop: workshop.name,
            dayOfWeek: date.toLocaleDateString('en-US', { weekday: 'long' })
          });
        });
      });

      // Limit total slots to avoid overwhelming the user
      if (slots.length >= 6) break;
    }

    // Return the first 6 slots
    return slots.slice(0, 6);
  };

  // Update client tools when local validations change
  useEffect(() => {
    let localLicensePlateValidated = false;
    let localMileageValidated = false;

    // Function to clean license plate
    const cleanLicensePlate = (plate: string): string => {
      return plate.replace(/[-.\s]/g, '').toUpperCase();
    };

    setClientToolsConfig({
      requestAction: async (parameters: { requestedAction: string }) => {
        const { requestedAction } = parameters;
        switch (requestedAction) {
          case 'SCHEDULEAPPOINTMENT': {
            // Get the first available slot for the appointment confirmation
            const slots = generateAvailableSlots(
              vehicleInfo.preferredArea || ''
            );
            const selectedSlot = slots[0] || {
              date: 'Next available date',
              time: '09:00 AM',
              workshop: 'AutoService Workshop'
            };

            onAppointmentScheduled();
            onFeedback({
              type: 'appointment',
              message: 'Service appointment scheduled',
              details: `Appointment scheduled for ${selectedSlot.date} at ${selectedSlot.time} at ${selectedSlot.workshop}`,
              success: true
            });
            return JSON.stringify({
              success: true,
              message: 'Appointment scheduled',
              data: {
                date: selectedSlot.date,
                time: selectedSlot.time,
                location: selectedSlot.workshop
              }
            });
          }
          case 'ORDERTIREPICKUP': {
            // Calculate delivery date (3-4 business days from today)
            const today = new Date();
            const deliveryDate = new Date(today);
            let businessDays = 0;

            while (businessDays < 4) {
              deliveryDate.setDate(deliveryDate.getDate() + 1);
              if (deliveryDate.getDay() !== 0 && deliveryDate.getDay() !== 6) {
                businessDays++;
              }
            }

            const deliveryDateStr = deliveryDate.toLocaleDateString('en-US', {
              month: 'long',
              day: 'numeric',
              year: 'numeric'
            });

            onFeedback({
              type: 'appointment',
              message: 'Tire order processed',
              details: `Tires will be delivered by ${deliveryDateStr} to ${
                vehicleInfo.preferredArea || 'preferred location'
              }`,
              success: true
            });
            return JSON.stringify({
              success: true,
              message: 'Tire order processed',
              data: {
                deliveryDate: deliveryDateStr,
                area: vehicleInfo.preferredArea || 'preferred location'
              }
            });
          }
          default: {
            onFeedback({
              type: 'info',
              message: `Action requested not available: ${requestedAction}`,
              details: 'Action requested not available',
              success: false
            });
            return JSON.stringify({
              success: false,
              data: null,
              message: `Action requested not available: '${requestedAction}'`
            });
          }
        }
      },

      requestInfo: async (parameters: { requestedInfo: string }) => {
        const { requestedInfo } = parameters;
        switch (requestedInfo) {
          case 'CUSTOMERPHONE': {
            onFeedback({
              type: 'info',
              message: 'Customer phone requested',
              details: `Phone: ${vehicleInfo.contactPhone || '694 555 1234'}`,
              success: true
            });
            return JSON.stringify({
              success: true,
              data: vehicleInfo.contactPhone || '694 555 1234',
              message: ''
            });
          }

          case 'DATETIME': {
            // Return current date and time
            const now = new Date();
            const options: Intl.DateTimeFormatOptions = {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              hour12: false
            };
            const dateTimeString = now.toLocaleString('en-GB', options);

            onFeedback({
              type: 'info',
              message: 'Date and time requested',
              details: `Current date and time: ${dateTimeString}`,
              success: true
            });

            return JSON.stringify({
              success: true,
              data: dateTimeString,
              message: ''
            });
          }

          case 'SERVICEHISTORY': {
            if (!localLicensePlateValidated || !localMileageValidated) {
              onFeedback({
                type: 'info',
                message: 'Service history requested',
                details:
                  'Vehicle validation required before accessing service history',
                success: false
              });
              return JSON.stringify({
                success: false,
                data: null,
                message: 'Please validate license plate and mileage first'
              });
            }

            onFeedback({
              type: 'info',
              message: 'Service history requested',
              details: `Service History: ${
                vehicleInfo.serviceHistory ||
                'Due for 2nd Service and Emissions Card renewal'
              }`,
              success: true
            });

            return JSON.stringify({
              success: true,
              data:
                vehicleInfo.serviceHistory ||
                'Due for 2nd Service and Emissions Card renewal',
              message: ''
            });
          }

          case 'TIRESIZE': {
            if (!localLicensePlateValidated) {
              onFeedback({
                type: 'info',
                message: 'Tire size requested',
                details:
                  'License plate validation required before accessing tire size',
                success: false
              });
              return JSON.stringify({
                success: false,
                data: null,
                message: 'Please validate license plate first'
              });
            }

            onFeedback({
              type: 'info',
              message: 'Tire size requested',
              details: `Tire Size: ${vehicleInfo.tireSize || '205/55R16'}`,
              success: true
            });

            return JSON.stringify({
              success: true,
              data: vehicleInfo.tireSize || '205/55R16',
              message: ''
            });
          }

          case 'SERVICETYPE': {
            // This sets the service type when the agent asks about it
            const serviceType = 'maintenance';
            onServiceTypeSelected(serviceType);

            onFeedback({
              type: 'info',
              message: 'Service type selected',
              details: `Type: ${
                serviceType === 'maintenance'
                  ? 'Service Appointment'
                  : 'Tire Order'
              }`,
              success: true
            });

            return JSON.stringify({
              success: true,
              data: serviceType,
              message: ''
            });
          }

          case 'AVAILABLEAPPOINTMENTS': {
            const area = vehicleInfo.preferredArea || '';
            const appointments = generateAvailableSlots(area);

            onFeedback({
              type: 'info',
              message: 'Available appointments requested',
              details: `Found ${
                appointments.length
              } available slots for area: ${area || 'any'}`,
              success: true
            });

            return JSON.stringify({
              success: true,
              data: appointments,
              message: ''
            });
          }

          default: {
            onFeedback({
              type: 'info',
              message: `Requested information not available: ${requestedInfo}`,
              details: 'Requested information not available',
              success: false
            });
            return JSON.stringify({
              success: false,
              data: null,
              message: `Requested information not available: '${requestedInfo}'`
            });
          }
        }
      },

      requestValidation: async (parameters: {
        requestedValidation: string;
        valueProvided: string;
      }) => {
        const { requestedValidation, valueProvided } = parameters;

        switch (requestedValidation) {
          case 'LICENSEPLATE': {
            const cleanedPlate = cleanLicensePlate(valueProvided);
            const formPlate = vehicleInfo.licensePlate
              ? cleanLicensePlate(vehicleInfo.licensePlate)
              : '';
            const isValid = formPlate === cleanedPlate;

            setIsLicensePlateValidated(isValid);
            localLicensePlateValidated = isValid;

            onFeedback({
              type: 'validation',
              message: 'License plate validation requested',
              details: `Plate provided: ${valueProvided} Result: ${
                isValid ? '✓ Plate matches' : '✗ Plate does not match'
              }`,
              success: isValid
            });

            return JSON.stringify({
              success: isValid,
              message: isValid
                ? 'License plate matches'
                : 'License plate does not match',
              validatedPlate: isValid ? cleanedPlate : null
            });
          }

          case 'MILEAGE': {
            if (!localLicensePlateValidated) {
              onFeedback({
                type: 'validation',
                message: 'Mileage validation requested',
                details:
                  'License plate validation required before validating mileage',
                success: false
              });
              return JSON.stringify({
                success: false,
                message: 'Please validate license plate first',
                validatedMileage: null
              });
            }

            try {
              const providedMileage = parseInt(
                valueProvided.replace(/[,.\s]/g, ''),
                10
              );
              const actualMileage = vehicleInfo.mileage;

              // Allow a small range for mileage validation (±500 km)
              const isValid = Math.abs(providedMileage - actualMileage) <= 500;

              setIsMileageValidated(isValid);
              localMileageValidated = isValid;

              onFeedback({
                type: 'validation',
                message: 'Mileage validation requested',
                details: `Mileage provided: ${valueProvided} Result: ${
                  isValid ? '✓ Mileage matches' : '✗ Mileage does not match'
                }`,
                success: isValid
              });

              return JSON.stringify({
                success: isValid,
                message: isValid ? 'Mileage matches' : 'Mileage does not match',
                validatedMileage: isValid ? providedMileage : null
              });
            } catch (error) {
              onFeedback({
                type: 'validation',
                message: 'Mileage validation error',
                details: 'Invalid mileage format',
                success: false
              });
              return JSON.stringify({
                success: false,
                message: 'Invalid mileage format',
                validatedMileage: null
              });
            }
          }

          case 'PREFERREDAREA': {
            // Always validate the area as valid
            const area = valueProvided.trim();

            onFeedback({
              type: 'validation',
              message: 'Preferred area validation',
              details: `Area provided: ${area}`,
              success: true
            });

            return JSON.stringify({
              success: true,
              message: 'Area preference recorded',
              validatedArea: area
            });
          }

          default: {
            onFeedback({
              type: 'validation',
              message: `Validation requested not available: ${requestedValidation}`,
              details: 'Validation requested not available',
              success: false
            });
            return JSON.stringify({
              success: false,
              data: null,
              message: `Validation requested not available: '${requestedValidation}'`
            });
          }
        }
      },

      END_CALL: async (parameters: { reason: string }) => {
        // Add feedback message
        onFeedback({
          type: 'close',
          message: 'Call ended by agent',
          details: `Reason: ${parameters.reason}`,
          success: true
        });

        return JSON.stringify({
          success: true,
          message: `Call ended`
        });
      }
    });
  }, [vehicleInfo, onFeedback, onAppointmentScheduled, onServiceTypeSelected]);

  // Process first message function to format the greeting
  const processFirstMessage = (message: string, agentName: string): string => {
    const getSalutation = () => {
      const hour = new Date().getHours();
      if (hour < 13) return 'Good morning';
      if (hour < 20) return 'Good afternoon';
      return 'Good evening';
    };

    return message
      .replace('{{salutationDayTime}}', getSalutation())
      .replace('{{agentName}}', agentName);
  };

  const conversation = websocketVirtualAgent({
    onConnect: () => {
      console.log('Connected to agent');
      setIsInitialized(true);
      setIsLoading(false);
    },
    onDisconnect: () => {
      console.log('Disconnected from agent');
      onFeedback({
        type: 'close',
        message: 'Hangup executed',
        success: true
      });
      setIsInitialized(false);
      setIsLoading(false);
      setIsLicensePlateValidated(false);
      setIsMileageValidated(false);
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (message: string, context?: any) => {
      console.error('Conversation error:', message, context);
      setIsLoading(false);
      if (onError) {
        onError(new Error(message));
      }
    },
    onMessage: message => {
      setMessages(prev => [
        ...prev,
        {
          source: message.source,
          message: message.message
        }
      ]);
      setTimeout(scrollToBottom, 0);
    },
    // Add client tools configuration
    clientTools: clientToolsConfig
  });

  const requestMicPermission = async (): Promise<boolean> => {
    try {
      await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });
      setHasPermission(true);
      return true;
    } catch (error) {
      console.error('Microphone permission denied:', error);
      setHasPermission(false);
      if (onError) {
        onError(new Error('Microphone permission denied'));
      }
      return false;
    }
  };

  const startConversation = useCallback(async (): Promise<void> => {
    if (!hasPermission) {
      const permitted = await requestMicPermission();
      if (!permitted) return;
    }

    try {
      setIsLoading(true);
      setIsLicensePlateValidated(false);
      setIsMileageValidated(false);
      await conversation.endSession();

      // Get signed URL
      let signedUrl;
      if (import.meta.env.VITE_NODE_ENV === 'production') {
        // In production, use the async function
        signedUrl = await getAuthenticatedWsUrl();
      } else {
        // In development, use the direct URL
        signedUrl = getVirtualAgentWsUrl();
      }

      // Start session with signed URL
      setMessages([]);
      await conversation.startSession({
        signedUrl,
        overrides: {
          agent: {
            prompt: {
              prompt: prompt
            },
            firstMessage: processFirstMessage(firstMessage, agentName),
            language: language,
            tools: [
              {
                id: 'requestAction',
                name: 'requestAction',
                description: 'Request a specific action to be performed',
                parameters: {
                  type: 'object',
                  properties: {
                    requestedAction: {
                      type: 'string',
                      description:
                        'The action to request (SCHEDULEAPPOINTMENT, ORDERTIREPICKUP)'
                    }
                  },
                  required: ['requestedAction']
                }
              },
              {
                id: 'requestInfo',
                name: 'requestInfo',
                description:
                  'Request specific information about the vehicle or customer',
                parameters: {
                  type: 'object',
                  properties: {
                    requestedInfo: {
                      type: 'string',
                      description:
                        'The type of information to request (CUSTOMERPHONE, DATETIME, SERVICEHISTORY, TIRESIZE, SERVICETYPE, AVAILABLEAPPOINTMENTS)'
                    },
                    serviceType: {
                      type: 'string',
                      description: 'Service type (maintenance or tires)',
                      enum: ['maintenance', 'tires']
                    }
                  },
                  required: ['requestedInfo']
                }
              },
              {
                id: 'requestValidation',
                name: 'requestValidation',
                description: 'Validate customer provided information',
                parameters: {
                  type: 'object',
                  properties: {
                    requestedValidation: {
                      type: 'string',
                      description:
                        'The type of validation to perform (LICENSEPLATE, MILEAGE, PREFERREDAREA)'
                    },
                    valueProvided: {
                      type: 'string',
                      description: 'The value provided by the customer'
                    }
                  },
                  required: ['requestedValidation', 'valueProvided']
                }
              },
              {
                id: 'END_CALL',
                name: 'END_CALL',
                description: 'Ends current conversation',
                parameters: {
                  type: 'object',
                  properties: {
                    reason: {
                      type: 'string',
                      description: 'The reason for ending the call'
                    }
                  },
                  required: []
                }
              }
            ]
          },
          tts: {
            voiceId: voice
          }
        }
      });
    } catch (error) {
      console.error('Failed to start conversation:', error);
      if (error instanceof Error && onError) {
        onError(error);
      }
    }
  }, [
    agentName,
    conversation,
    firstMessage,
    hasPermission,
    language,
    onError,
    prompt,
    voice
  ]);

  const endConversation = useCallback(async (): Promise<void> => {
    try {
      await conversation.endSession();
    } catch (error) {
      console.error('Failed to end conversation:', error);
      if (error instanceof Error && onError) {
        onError(error);
      }
    }
  }, [conversation, onError]);

  // Effect to initialize scroll when messages are mounted
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Effect to access the microphone
  useEffect(() => {
    void requestMicPermission();
    return () => {
      if (isInitialized) {
        void endConversation();
      }
    };
  }, [isInitialized]);

  return (
    <div className="flex flex-col items-center p-0">
      <Row className="mb-2 mt-2">
        <Col className="pe-0" style={{ maxWidth: '170px', width: '170px' }}>
          <div className="d-flex bg-primary rounded-5 w-100 justify-content-center">
            {!isInitialized ? (
              <Button
                onClick={() => void startConversation()}
                className="p-2 text-decoration-none text-white"
                variant="link"
                disabled={!hasPermission || isLoading}
              >
                <FontAwesomeIcon icon={faPhoneVolume} className="me-2 fs-8" />
                {isLoading ? 'Connecting...' : 'Start call'}
              </Button>
            ) : (
              <Button
                onClick={() => void endConversation()}
                className="p-2 text-decoration-none text-white"
                variant="link"
              >
                <FontAwesomeIcon icon={faPhoneSlash} className="me-2 fs-8" />
                End call
              </Button>
            )}
          </div>
        </Col>
        <Col className="d-flex justify-content-end">
          <div className="d-flex justify-content-end text-end fs-10 me-2">
            <Row className="text-body-secondary">
              <div className="pt-1">
                Connected:
                {conversation.status === 'connected' ? (
                  <FontAwesomeIcon
                    icon={faPhoneVolume}
                    className="mx-2 me-0 fs-10 text-success"
                  />
                ) : (
                  <FontAwesomeIcon
                    icon={faPhoneSlash}
                    className="mx-2 me-0 fs-10 text-danger"
                  />
                )}
              </div>
              <div>
                {conversation.status === 'connected' ? (
                  <>
                    Status:
                    {conversation.isSpeaking ? (
                      <FontAwesomeIcon
                        icon={faMicrophone}
                        className="mx-2 me-1 fs-10 text-success"
                      />
                    ) : (
                      <FontAwesomeIcon
                        icon={faMicrophoneSlash}
                        className="mx-1 me-0 fs-10 text-danger"
                      />
                    )}
                  </>
                ) : null}
              </div>
            </Row>
          </div>
        </Col>
      </Row>

      {/* Status indicators */}
      <Row className="w-100 mb-2">
        <Col>
          <div className="d-flex gap-2">
            <div
              className={`badge ${
                isLicensePlateValidated ? 'bg-success' : 'bg-secondary'
              } d-flex align-items-center`}
            >
              <FontAwesomeIcon icon={faCar} className="me-1" />
              <span>License Plate {isLicensePlateValidated ? '✓' : '⦻'}</span>
            </div>
            <div
              className={`badge ${
                isMileageValidated ? 'bg-success' : 'bg-secondary'
              } d-flex align-items-center`}
            >
              <span>Mileage {isMileageValidated ? '✓' : '⦻'}</span>
            </div>
          </div>
        </Col>
      </Row>

      <div
        ref={messagesContainerRef}
        className="p-3 pt-2 pb-2 border rounded scrollbar bg-body-emphasis fs-9 lh-sm shadow-sm w-100"
        style={{
          height: '190px',
          overflowY: 'auto',
          scrollBehavior: 'smooth'
        }}
      >
        {messages.length > 0 && (
          <div className="w-full space-y-2">
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`p-2 rounded mb-2 ${
                  msg.source === 'user' ? 'bg-body' : 'bg-body-emphasis'
                }`}
              >
                <strong>{msg.source === 'user' ? 'Customer' : 'Agent'}:</strong>
                &nbsp;
                {msg.message}
              </div>
            ))}
          </div>
        )}
      </div>
      {!hasPermission && (
        <div className="fs-9 p-2 bg-body rounded-lg mt-2 w-100 text-center">
          Microphone permission is required for conversation
        </div>
      )}
    </div>
  );
};

export default HVAAyvensConversationWidget;
